import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STEPS = [
  { key: "pin", title: "Pin Ryan to your toolbar", desc: "Click the puzzle icon in Chrome, then the pin next to Ryan. Tick the box when done.", manual: true },
  { key: "first_save", title: "Save your first item", desc: "Go to Claude, ChatGPT, or Gemini and click the pin button on any message." },
  { key: "add_volume", title: "Add it to a volume", desc: "Organize your save into a volume when pinning or from the dashboard." },
];

const StepCheckbox = ({ checked, onClick, testId }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onClick}
    disabled={!onClick}
    data-testid={testId}
    className={`w-6 h-6 min-w-[24px] min-h-[24px] aspect-square shrink-0 flex items-center justify-center border-2 rounded-[3px] transition-colors ${
      checked ? "border-[#7AB8A0] bg-[#7AB8A0]" : "border-[#D9C8A6] bg-transparent"
    } ${onClick ? "cursor-pointer hover:border-[#E8A030]" : "cursor-default"}`}
  >
    {checked && (
      <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="#0A0600" strokeWidth="2.5">
        <path d="M3 8.5l3.2 3L13 4.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

const StepRow = ({ step, done, onComplete }) => (
  <div className="flex items-start gap-4" data-testid={`welcome-step-${step.key}`}>
    <div className="pt-0.5">
      <StepCheckbox
        checked={done}
        onClick={step.manual && !done ? onComplete : undefined}
        testId={`welcome-checkbox-${step.key}`}
      />
    </div>
    <div className="flex-1">
      <p className={`text-[15px] font-medium ${done ? "text-[#7AB8A0] line-through" : "text-[#FDF8EE]"}`}>{step.title}</p>
      <p className="text-[13px] text-[#E6D9BF] mt-1 leading-relaxed">{step.desc}</p>
    </div>
  </div>
);

const Welcome = () => {
  const { user, login, loading: authLoading } = useAuth();
  const [onboarding, setOnboarding] = useState({ pin: false, first_save: false, add_volume: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/onboarding/status`).then(res => {
        setOnboarding(res.data);
        setLoaded(true);
      }).catch(() => setLoaded(true));
      axios.post(`${API}/events/track`, { event: "install" }).catch(() => {});
    }
  }, [user]);

  const completeStep = async (step) => {
    setOnboarding(prev => ({ ...prev, [step]: true }));
    try {
      await axios.put(`${API}/onboarding/complete-step`, { step });
      await axios.post(`${API}/events/track`, { event: "onboarding_step_completed", metadata: { step } });
    } catch (e) { /* silent */ }
  };

  const doneCount = STEPS.filter(s => onboarding[s.key]).length;
  const allDone = doneCount === STEPS.length;

  return (
    <div className="min-h-screen bg-[#0A0600] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        <div className="text-center mb-10">
          <div className="text-[#E8A030] text-3xl mb-3">◈</div>
          <h1 className="font-serif text-3xl font-black text-[#FDF8EE] tracking-[-1px]">Welcome to Ryan</h1>
          <p className="text-[15px] text-[#E6D9BF] mt-3 leading-relaxed">
            Everything you save, in one place you can actually find.
          </p>
        </div>

        {!user && !authLoading ? (
          <div className="text-center">
            <p className="text-sm text-[#E6D9BF] mb-6">Sign in to get started.</p>
            <button onClick={login} className="btn-primary text-sm px-8 py-3" data-testid="welcome-login-btn">
              Sign in with Google
            </button>
          </div>
        ) : authLoading || !loaded ? (
          <div className="text-center"><div className="spinner-ember mx-auto"></div></div>
        ) : (
          <div className="space-y-8">
            <div className="bg-[rgba(232,160,48,0.05)] border border-[rgba(232,160,48,0.25)] p-6" data-testid="welcome-checklist">
              <div className="flex items-center justify-between mb-6">
                <p className="font-mono text-[11px] text-[#F5ECD0] uppercase tracking-[1px]">Three quick steps to get started</p>
                <span className="font-mono text-[11px] text-[#E8A030]" data-testid="welcome-progress">{doneCount}/{STEPS.length}</span>
              </div>
              <div className="space-y-5">
                {STEPS.map(step => (
                  <StepRow key={step.key} step={step} done={!!onboarding[step.key]} onComplete={() => completeStep(step.key)} />
                ))}
              </div>
              {user.subscription_status !== "active" && (
                <div className="mt-6 pt-4 border-t border-[rgba(232,160,48,0.2)]">
                  <p className="font-mono text-[11px] text-[#F0B85A] uppercase tracking-[0.5px]" data-testid="welcome-free-limit">
                    You can save 10 items on the free plan.
                  </p>
                </div>
              )}
            </div>

            <div className="text-center space-y-3">
              {allDone && <p className="text-sm text-[#7AB8A0] font-medium" data-testid="welcome-all-set">You're all set!</p>}
              <a href="/dashboard" className="btn-primary inline-block px-10 py-3 text-sm" data-testid="welcome-go-dashboard">
                Continue to Dashboard →
              </a>
              {!allDone && (
                <p className="text-[12px] text-[#D9C8A6]">You can finish the remaining steps anytime from your dashboard.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Welcome;
